import {
    Button,
    Col,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
    Row,
} from 'reactstrap';
import { CoaDto, UserManagement } from '../../common/Types';
import { useForm, Controller } from 'react-hook-form';
import { ErrorMessage } from '@hookform/error-message';
import React, { ChangeEvent, useEffect, useState } from 'react';

import { NavLink } from 'react-router-dom';
import PageTitle from '../../components/PageTitle';
import { authService } from '../../services/Auth';
import { coaService } from '../../services/CoaAdministration';
import { securityService } from '../../services/SecurityService';
import { toast } from 'react-toastify';

interface ModalDetail {
    header: string;
    confirmationButtonText: string;
    description: string;
    userId: string;
    action: (userId: string) => void;
}

const Users = () => {
    const [users, setUsers] = useState<UserManagement[]>([]);
    const [coas, setCoas] = useState<CoaDto[]>([]);
    const [reloading, reload] = useState(true);
    const [showRemoved, setShowRemoved] = useState(false);
    const [showModal, setModal] = useState(false);
    const [showFormModal, setFormModal] = useState(false);
    const [addEditUser, setAddEditUser] = useState<UserManagement>({
        email: '',
        isRemoved: false,
        name: '',
        roles: [],
        userId: '',
        firstName: '',
        lastName: '',
        coaIds: [],
    });
    const {
        handleSubmit,
        control,
        reset,
        formState: { errors },
        watch,
        getValues,
    } = useForm();

    const watchRole = watch('roles');

    useEffect(() => {
        if (reloading) {
            reload(false);
            securityService
                .getCaahepAndCoaUsers()
                .then((response) => {
                    setUsers(response || []);
                })
                .catch(() => {
                    toast.error('There was an issue loading users.');
                });
        }
    }, [setUsers, reloading]);

    useEffect(() => {
        coaService
            .getCoas()
            .then((response) => {
                setCoas(response.filter((c) => c.isActive === true) || []);
            })
            .catch(() => {
                toast.error('There was an issue loading CoAs.');
            });
    }, [setCoas]);

    const showUser = (user): boolean => {
        return !user.isRemoved || (user.isRemoved && showRemoved);
    };

    const toggleModal = () => setModal(!showModal);
    const toggleFormModal = () => setFormModal(!showFormModal);

    const removeUser = (userId: string): void => {
        const toastId = toast.info('Removing user...');

        securityService
            .removeUser(userId)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'User removed!',
                });
                reload(true);
                setModal(false);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'There was an issue removing the user.',
                });
            });
    };

    const reinstateUser = (userId: string): void => {
        const toastId = toast.info('Reinstating user...');

        securityService
            .reinstateUser(userId)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'User reinstated!',
                });
                reload(true);
                setModal(false);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'There was an issue reinstating the user.',
                });
            });
    };

    const initiatePasswordReset = (userId: string): void => {
        const toastId = toast.info('Sending password reset instructions...');

        securityService
            .initiatePasswordReset(userId)
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Password reset instructions sent!',
                });
                reload(true);
                setModal(false);
            })
            .catch(() => {
                toast.update(toastId, {
                    type: 'error',
                    render: 'There was an issue sending password reset instructions.',
                });
            });
    };

    const [modalDetail, setModalDetail] = useState<ModalDetail>({
        action: removeUser,
        confirmationButtonText: 'Remove User',
        description: 'Test',
        header: 'Confirm User Removal',
        userId: '',
    });

    const triggerModalConfirmation = (newModalDetail: ModalDetail): void => {
        setModalDetail(newModalDetail);
        toggleModal();
    };

    const startUserManage = (user: UserManagement): void => {
        setAddEditUser(user);
        reset(user);
        toggleFormModal();
    };

    const isSelf = (user: UserManagement): boolean => {
        return authService.getProfile()!.sub === user.userId;
    };

    const saveUser = (user: any): void => {
        const toastId = toast.info('Saving...');

        securityService
            .createOrUpdateUser(new UserManagement(user))
            .then(() => {
                toast.update(toastId, {
                    type: 'success',
                    render: 'Save successful!',
                });
                reload(true);
                setFormModal(false);
            })
            .catch((error) => {
                toast.update(toastId, {
                    type: 'error',
                    render: error.message || 'There was an issue saving the user information.',
                });
            });
    };

    const hasRoleSelected = (role: string): boolean => {
        const user = getValues();

        if (user && user.roles && !Array.isArray(user.roles)) {
            user.roles = user.roles.split(',');
        }

        return user && user.roles && user.roles.filter((r) => r === role).length > 0;
    };

    const changeRoleSelection = (e: ChangeEvent<HTMLInputElement>, role: string): void => {
        const user = getValues();

        if (user && user.roles && !Array.isArray(user.roles)) {
            user.roles = user.roles.split(',');
        }

        if (e.target.checked) {
            if (!user.roles || (user.roles && user.roles.filter((r) => r === role).length === 0)) {
                user.roles = [...user.roles, role];
            }
        }

        if (!e.target.checked && user.roles) {
            const index = user.roles.indexOf(role);

            if (index >= 0) {
                user.roles.splice(index, 1);
            }
        }

        if (user.roles && user.roles.filter((r) => r === 'CoA Administrator').length === 0) {
            user.coaIds = [];
        }

        reset(user);
    };

    const hasCoaSelected = (id: number): boolean => {
        const user = getValues();

        if (user && user.coaIds && !Array.isArray(user.coaIds)) {
            user.coaIds = user.coaIds.split(',').map((c) => {
                return +c;
            });
        }

        return (user && user.coaIds && user.coaIds.filter((c) => c === id).length > 0) || false;
    };

    const changeCoaSelection = (e: ChangeEvent<HTMLInputElement>, id: number): void => {
        const user = getValues();

        if (user && user.coaIds && !Array.isArray(user.coaIds)) {
            user.coaIds = user.coaIds.split(',').map((c) => {
                return +c;
            });
        }

        if (e.target.checked) {
            if (!user.coaIds || (user.coaIds && user.coaIds.filter((c) => c === id).length === 0)) {
                user.coaIds = [...user.coaIds, id];
            }
        }

        if (!e.target.checked && user.coaIds) {
            const index = user.coaIds.indexOf(id);

            if (index >= 0) {
                user.coaIds.splice(index, 1);
            }
        }
        reset(user);
    };

    return (
        <>
            <Row>
                <Col>
                    <PageTitle title="Users" />
                </Col>
            </Row>
            <Row>
                <Col>
                    <div className={'card-box'} style={{ backgroundColor: '#fff', padding: '1.5rem', marginBottom: '1.5rem', border: '1px solid rgba(0,0,0,0.125)', borderRadius: '0.25rem' }}>
                        <div className={'responsive-table-plugin'}>
                            <div className={'table-wrapper'}>
                                <div className={'btn-toolbar clearfix mb-3'}>
                                    <div className={'btn-group'}>
                                        <Button
                                            color="info"
                                            type="button"
                                            className="add"
                                            onClick={() =>
                                                startUserManage({
                                                    email: '',
                                                    isRemoved: false,
                                                    name: '',
                                                    roles: [],
                                                    userId: '',
                                                    firstName: '',
                                                    lastName: '',
                                                    coaIds: [],
                                                })
                                            }
                                        >
                                            <i className={'mdi mdi-account-plus'} />
                                            <span className={'ml-1'}>Add New User</span>
                                        </Button>
                                    </div>
                                    <div className={'btn-group pull-right'}>
                                        <button
                                            type="button"
                                            className={`btn btn-default ${showRemoved && 'btn-primary'}`}
                                            onClick={() => setShowRemoved(!showRemoved)}
                                        >
                                            Show Removed Users
                                        </button>
                                    </div>
                                </div>
                                <div className={'table-responsive mt-2'}>
                                    <table className={'table table-hover'}>
                                        <thead>
                                            <tr>
                                                <th style={{ minWidth: '200px', whiteSpace: 'nowrap' }}>Name</th>
                                                <th>Email</th>
                                                <th>Role</th>
                                                <th>CoA Association</th>
                                                <th></th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {users &&
                                                users.map(
                                                    (user) =>
                                                        showUser(user) && (
                                                            <tr
                                                                key={user.userId}
                                                                className={`${user.isRemoved && 'table-danger'}`}
                                                            >
                                                                <td style={{ whiteSpace: 'nowrap' }}>
                                                                    {isSelf(user) && (
                                                                        <span
                                                                            className={`mr-2 text-white`}
                                                                            style={{ 
                                                                                fontSize: '10px', 
                                                                                padding: '2px 6px',
                                                                                backgroundColor: '#264891',
                                                                                borderRadius: '3px',
                                                                                display: 'inline-block',
                                                                                lineHeight: '1',
                                                                                fontWeight: '600',
                                                                                verticalAlign: 'middle'
                                                                            }}
                                                                        >
                                                                            YOU
                                                                        </span>
                                                                    )}
                                                                    {user.name}
                                                                </td>
                                                                <td className="user-email">{user.email}</td>
                                                                <td>
                                                                    {user.roles.map((role) => (
                                                                        <div key={role}>
                                                                            <span>{role}</span>
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td>
                                                                    {user.coaIds.map((coaId) => (
                                                                        <div key={coaId}>
                                                                            <span>
                                                                                {coas.filter((c) => c.id === coaId)
                                                                                    .length > 0 && (
                                                                                    <span>
                                                                                        {
                                                                                            coas.filter(
                                                                                                (c) => c.id === coaId,
                                                                                            )[0].coaTitle
                                                                                        }
                                                                                    </span>
                                                                                )}
                                                                            </span>
                                                                        </div>
                                                                    ))}
                                                                </td>
                                                                <td className={'text-right'}>
                                                                    {!isSelf(user) && (
                                                                        <div className={'btn btn-group'}>
                                                                            <button
                                                                                type="button"
                                                                                className={'btn btn-sm btn-default'}
                                                                                onClick={() => startUserManage(user)}
                                                                            >
                                                                                <i className={'mdi mdi-account-edit'} />
                                                                                <span className={'ml-1'}>Edit</span>
                                                                            </button>
                                                                            {!user.isRemoved && (
                                                                                <button
                                                                                    type="button"
                                                                                    className={'btn btn-sm btn-default'}
                                                                                    onClick={() =>
                                                                                        triggerModalConfirmation({
                                                                                            action: initiatePasswordReset,
                                                                                            confirmationButtonText:
                                                                                                'Send Password Reset Instruction',
                                                                                            description: `Please click the button below to send password reset instructions to  
                                                                                          ${user.name} (${user.email}).`,
                                                                                            header: 'Password Reset',
                                                                                            userId: user.userId,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <i
                                                                                        className={
                                                                                            'mdi mdi-account-key'
                                                                                        }
                                                                                    />
                                                                                    <span className={'ml-1'}>
                                                                                        Reset Password
                                                                                    </span>
                                                                                </button>
                                                                            )}
                                                                            {user.isRemoved && (
                                                                                <button
                                                                                    type="button"
                                                                                    className={'btn btn-sm btn-default'}
                                                                                    onClick={() =>
                                                                                        triggerModalConfirmation({
                                                                                            action: reinstateUser,
                                                                                            confirmationButtonText:
                                                                                                'Confirm Reinstatement',
                                                                                            description: `Please click the button below to confirm reinstatement of the user 
                                                                                          ${user.name} (${user.email}). Once completed, the user will be 
                                                                                          allows to log into the system.`,
                                                                                            header: 'Reinstate User',
                                                                                            userId: user.userId,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <i
                                                                                        className={
                                                                                            'mdi mdi-account-remove'
                                                                                        }
                                                                                    />
                                                                                    <span className={'ml-1'}>
                                                                                        Reinstate
                                                                                    </span>
                                                                                </button>
                                                                            )}
                                                                            {!user.isRemoved && (
                                                                                <button
                                                                                    type="button"
                                                                                    className={'btn btn-sm btn-danger'}
                                                                                    onClick={() =>
                                                                                        triggerModalConfirmation({
                                                                                            action: removeUser,
                                                                                            confirmationButtonText:
                                                                                                'Confirm Removal',
                                                                                            description: `Please click the button below to confirm removal of the user 
                                                                            ${user.name} (${user.email}). Once completed, the user will no longer be permitted
                                                                            to log into the system.`,
                                                                                            header: 'Remove User',
                                                                                            userId: user.userId,
                                                                                        })
                                                                                    }
                                                                                >
                                                                                    <i
                                                                                        className={
                                                                                            'mdi mdi-account-plus'
                                                                                        }
                                                                                    />
                                                                                    <span className={'ml-1'}>
                                                                                        Remove
                                                                                    </span>
                                                                                </button>
                                                                            )}
                                                                        </div>
                                                                    )}
                                                                    {isSelf(user) && (
                                                                        <div className={'btn btn-group'}>
                                                                            <NavLink
                                                                                className={'btn btn-default'}
                                                                                to={`/profile`}
                                                                            >
                                                                                <i
                                                                                    className={
                                                                                        'mdi mdi-account-card-details'
                                                                                    }
                                                                                />
                                                                                <span className={'ml-1'}>
                                                                                    Manage My Account
                                                                                </span>
                                                                            </NavLink>
                                                                        </div>
                                                                    )}
                                                                </td>
                                                            </tr>
                                                        ),
                                                )}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            <Modal isOpen={showModal} toggle={toggleModal}>
                <ModalHeader toggle={toggleModal}>{modalDetail.header}</ModalHeader>
                <ModalBody>{modalDetail.description}</ModalBody>
                <ModalFooter>
                    <Button type="button" color={'primary'} onClick={() => modalDetail.action(modalDetail.userId)}>
                        {modalDetail.confirmationButtonText}
                    </Button>
                    <Button type="button" color={'link'} onClick={() => toggleFormModal()}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
            <Modal isOpen={showFormModal} toggle={toggleFormModal} size={'lg'}>
                <Form noValidate onSubmit={handleSubmit((values) => saveUser(values as UserManagement))}>
                    <ModalHeader toggle={toggleFormModal}>{`${
                        addEditUser.userId === '' ? 'Add New' : 'Edit'
                    } User`}</ModalHeader>
                    <ModalBody>
                        <Controller
                            name="userId"
                            control={control}
                            render={({ field }) => <input type="hidden" {...field} value={field.value ?? ''} />}
                        />
                        <FormGroup className={'row'}>
                            <Label className={'col-form-label col-md-3'}>First Name</Label>
                            <Col md={9}>
                                <Controller
                                    name="firstName"
                                    control={control}
                                    rules={{ required: 'First Name is required.' }}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            {...field}
                                            value={field.value ?? ''}
                                            invalid={!!errors.firstName}
                                        />
                                    )}
                                />
                                {errors.firstName?.message && (
                                    <div className={'invalid-feedback'}>{String(errors.firstName.message)}</div>
                                )}
                            </Col>
                        </FormGroup>
                        <FormGroup className={'row'}>
                            <Label className={'col-form-label col-md-3'}>Last Name</Label>
                            <Col md={9}>
                                <Controller
                                    name="lastName"
                                    control={control}
                                    rules={{ required: 'Last Name is required.' }}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            {...field}
                                            value={field.value ?? ''}
                                            invalid={!!errors.lastName}
                                        />
                                    )}
                                />
                                {errors.lastName?.message && (
                                    <div className={'invalid-feedback'}>{String(errors.lastName.message)}</div>
                                )}
                            </Col>
                        </FormGroup>
                        <FormGroup className={'row'}>
                            <Label className={'col-form-label col-md-3'}>Email</Label>
                            <Col md={9}>
                                <Controller
                                    name="email"
                                    control={control}
                                    rules={{
                                        required: 'Email is required.',
                                        pattern: {
                                            value: /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                                            message: 'Please enter a valid email address.',
                                        },
                                    }}
                                    render={({ field }) => (
                                        <Input
                                            type="text"
                                            {...field}
                                            value={field.value ?? ''}
                                            invalid={!!errors.email}
                                        />
                                    )}
                                />
                                {errors.email?.message && (
                                    <div className={'invalid-feedback'}>{String(errors.email.message)}</div>
                                )}
                            </Col>
                        </FormGroup>
                        <FormGroup className={'row'}>
                            <Label className={'col-form-label col-md-3'}>Role</Label>
                            <Col md={9}>
                                <Controller
                                    name="roles"
                                    control={control}
                                    rules={{ required: 'A role is required.' }}
                                    render={({ field }) => (
                                        <input
                                            type="hidden"
                                            {...field}
                                            value={
                                                Array.isArray(field.value) ? field.value.join(',') : (field.value ?? '')
                                            }
                                        />
                                    )}
                                />
                                <div className={'custom-control custom-checkbox mb-2'}>
                                    <input
                                        type="checkbox"
                                        id="admin"
                                        className={'custom-control-input'}
                                        value={'Administrator'}
                                        checked={hasRoleSelected('Administrator')}
                                        onChange={(e) => changeRoleSelection(e, 'Administrator')}
                                    />
                                    <Label className={'custom-control-label'} for="admin">
                                        Administrator
                                    </Label>
                                </div>
                                <div className={'custom-control custom-checkbox mb-2'}>
                                    <input
                                        type="checkbox"
                                        id="caahep"
                                        className={'custom-control-input'}
                                        value={'CAAHEP General User'}
                                        checked={hasRoleSelected('CAAHEP General User')}
                                        onChange={(e) => changeRoleSelection(e, 'CAAHEP General User')}
                                    />
                                    <Label className={'custom-control-label'} for="caahep">
                                        CAAHEP General User
                                    </Label>
                                </div>
                                <div className={'custom-control custom-checkbox mb-2'}>
                                    <input
                                        type="checkbox"
                                        id="coa"
                                        className={'custom-control-input'}
                                        value={'CoA Administrator'}
                                        checked={hasRoleSelected('CoA Administrator')}
                                        onChange={(e) => changeRoleSelection(e, 'CoA Administrator')}
                                    />
                                    <Label className={'custom-control-label'} for="coa">
                                        CoA Administrator
                                    </Label>
                                </div>
                            </Col>
                        </FormGroup>
                        {watchRole && watchRole.toString().indexOf('CoA Administrator') >= 0 && (
                            <FormGroup className={'row'}>
                                <Label className={'col-form-label col-md-3'}>CoA(s)</Label>
                                <Col md={9}>
                                    <Controller
                                        name="coaIds"
                                        control={control}
                                        rules={{ required: 'A CoA selection is required.' }}
                                        render={({ field }) => (
                                            <input
                                                type="hidden"
                                                {...field}
                                                value={
                                                    Array.isArray(field.value)
                                                        ? field.value.join(',')
                                                        : (field.value ?? '')
                                                }
                                            />
                                        )}
                                    />
                                    {coas.map(
                                        (coa, i) =>
                                            coa.coaTitle && (
                                                <div key={i} className={'custom-control custom-checkbox mb-2'}>
                                                    <input
                                                        type="checkbox"
                                                        id={`coa${i}`}
                                                        className={'custom-control-input'}
                                                        value={coa.id}
                                                        checked={hasCoaSelected(coa.id)}
                                                        onChange={(e) => changeCoaSelection(e, coa.id)}
                                                    />
                                                    <Label className={'custom-control-label'} for={`coa${i}`}>
                                                        {coa.coaTitle}
                                                    </Label>
                                                    <div className={'d-flex flex-column'}>
                                                        {coa.professions &&
                                                            coa.professions.map((prof) => (
                                                                <div
                                                                    className={'pt-2 pl-2 d-flex flex-column'}
                                                                    key={prof.professionId}
                                                                >
                                                                    <span>{`Profession: ${prof.title}`}</span>
                                                                    {prof.concentrations &&
                                                                        prof.concentrations.length > 0 && (
                                                                            <div
                                                                                className={
                                                                                    'pt-2 pl-2 d-flex flex-column'
                                                                                }
                                                                            >
                                                                                <span>{`Concentrations`}</span>
                                                                                {prof.concentrations.map((c) => (
                                                                                    <span
                                                                                        key={c.concentrationId}
                                                                                        className={'pt-2 pl-2'}
                                                                                    >
                                                                                        {c.title}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                    {prof.addOnTracks &&
                                                                        prof.addOnTracks.length > 0 && (
                                                                            <div
                                                                                className={
                                                                                    'pt-2 pl-2 d-flex flex-column'
                                                                                }
                                                                            >
                                                                                <span>{`Add-On Tracks`}</span>
                                                                                {prof.addOnTracks.map((a) => (
                                                                                    <span
                                                                                        key={a.addOnTrackId}
                                                                                        className={'pt-2 pl-2'}
                                                                                    >
                                                                                        {a.title}
                                                                                    </span>
                                                                                ))}
                                                                            </div>
                                                                        )}
                                                                </div>
                                                            ))}
                                                    </div>
                                                </div>
                                            ),
                                    )}
                                    {errors.coaIds?.message && (
                                        <div className={'invalid-feedback'}>{String(errors.coaIds.message)}</div>
                                    )}
                                </Col>
                            </FormGroup>
                        )}
                    </ModalBody>
                    <ModalFooter>
                        <Button type="submit" color={'primary'}>
                            Save Changes
                        </Button>
                        <Button type="button" color={'link'} onClick={() => toggleFormModal()}>
                            Cancel
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>
        </>
    );
};

export default Users;
